/* eslint-disable unicorn/prefer-top-level-await */
import * as React from "react";
import * as Sentry from "@sentry/react-router";
import { createInstance as createI18nextInstance } from "i18next";
import type { i18n } from "i18next";
import i18nextLanguageDetector from "i18next-browser-languagedetector";
import i18nextFetchBackend from "i18next-fetch-backend";
import { hydrateRoot } from "react-dom/client";
import { I18nextProvider, initReactI18next } from "react-i18next";
import { HydratedRouter } from "react-router/dom";
import { getInitialNamespaces } from "./i18n/client";
import { i18nextInitOptions } from "./i18n/config";

// The viewer probe is off in production builds. Dev turns it on before
// hydration so you can call `window.__viewerPerf` in the console. CI turns
// it on in `viewer-perf.spec.ts` only. There is no in-app overlay.
if (process.env.NODE_ENV !== "production") {
	(window as Window & { __VIEWER_PERF__?: boolean }).__VIEWER_PERF__ = true;
}

if (process.env.NODE_ENV === "production") {
	import("./monitoring/monitoring.client").then(({ init }) => init());
}

initI18next().then((i18nextInstance) => {
	React.startTransition(() => {
		hydrateRoot(
			document,
			<React.StrictMode>
				<I18nextProvider i18n={i18nextInstance}>
					{/* `sentryOnError` integrates with RR7's `onError` hook to capture
					 * client-side route errors. Replaces the `withSentry(App)` wrapper
					 * that the old Remix Sentry SDK used to expose. */}
					<HydratedRouter onError={Sentry.sentryOnError} />
				</I18nextProvider>
			</React.StrictMode>,
		);
	});
});

async function initI18next(): Promise<i18n> {
	const i18nextInstance = createI18nextInstance()
		.use(initReactI18next) // Tell i18next to use the react-i18next plugin
		.use(i18nextLanguageDetector) // Setup a client-side language detector
		.use(i18nextFetchBackend); // Setup your backend;

	await i18nextInstance.init({
		...i18nextInitOptions,
		ns: getInitialNamespaces(),
		backend: {
			loadPath: "/resources/locales/{{lng}}/{{ns}}",
		},
		detection: {
			// Here only enable htmlTag detection, we'll detect the language only
			// server-side with serverI18n, via the `<html lang>` attribute
			// we can communicate to the client the language detected server-side
			order: ["htmlTag"],
			// Because we only use htmlTag, there's no reason to cache the language
			// on the browser, so we disable it
			caches: [],
		},
	});

	return i18nextInstance;
}
